"use client";
import Link from "next/link";
import { Calendar, ArrowRight } from "lucide-react";
import { useMemo } from "react";
import { createLookupObject } from "@/lib/helper";
import { ICMS } from "@/store/home/homeModel";
import Banner from "@/components/Banner";
import { OptimizedImage } from "@/components/common/OptimizedImage";

interface BlogProps {
  cmsData: ICMS[];
  bannerData: any[];
  blogData: any[];
}

export default function Blog({ cmsData, bannerData, blogData }: BlogProps) {
  const lookUpCMSData = useMemo(() => {
    return createLookupObject(cmsData || [], "Name");
  }, [cmsData]);

  const banner =
    bannerData?.filter((banner: any) => banner?.BannerType === "blogs") || [];

  return (
    <>
      {/* Hero Section */}
      <Banner BannerList={banner} />

      {/* Blog Grid */}
      {blogData && blogData?.length > 0 && (
        <section className="py-20 bg-gradient-to-b from-white to-[#F3EBDD]/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogData.map((post: any) => (
                <Link
                  key={post.ID}
                  href={`/blogs/${post.BlogURL}/`}
                  className="group"
                >
                  <article className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all h-full flex flex-col">
                    <div className="relative h-64 overflow-hidden">
                      <OptimizedImage
                        src={post.Image}
                        alt={post.Title}
                        width={600}
                        height={400}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-4 text-sm text-[#5A3A1E]/85 mb-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {post.BlogDate ? post.BlogDate : "Recent"}
                        </div>
                      </div>
                      <h3
                        className="text-2xl mb-3 text-[#5A3A1E] group-hover:text-[#BF5500] transition-colors line-clamp-2"
                        style={{ fontFamily: "Playfair Display, serif" }}
                      >
                        {post.Title}
                      </h3>
                      <p className="text-[#5A3A1E]/85 mb-4 line-clamp-3 flex-1">
                        {post.ShortDescription}
                      </p>
                      <div className="flex items-center gap-2 text-[#BF5500] font-medium group-hover:gap-4 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
